import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Edit, Loader2, Plus, Trash } from 'lucide-react';
import { useState } from 'react';
import { type AccountAddress, accountApi } from '@/api/commercial/accounts';
import { proposalApi } from '@/api/commercial/proposals';
import { AddressFormDialog } from '@/components/commercial/accounts/create/address-form-dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { LocalAddress } from '@/context/create-account-flow';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface StepDeliveryProps {
  accountId: string | undefined;
  proposalId?: string;
  selectedAddressId?: string | null;
  onSelectAddress: (addressId: string | null) => void;
}

function formatAddressParts(address: AccountAddress) {
  const parts: string[] = [];
  if (address.street && address.number && address.district) {
    parts.push(`${address.street}, ${address.number}, ${address.district}`);
  }
  if (address.city && address.state && address.zip_code) {
    parts.push(`${address.city} - ${address.state}, ${address.zip_code}`);
  }
  return parts;
}

export function StepDelivery({
  accountId,
  proposalId,
  selectedAddressId,
  onSelectAddress,
}: StepDeliveryProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<LocalAddress | null>(null);
  const [deleteAddressId, setDeleteAddressId] = useState<string | null>(null);

  const { data: addresses = [], isLoading } = useQuery({
    queryKey: ['addresses', accountId],
    queryFn: () => accountApi.findAddresses(accountId!),
    enabled: !!accountId,
  });

  const createAddressMutation = useMutation({
    mutationFn: (data: LocalAddress) => {
      if (!accountId) throw new Error('Account ID is required');
      return accountApi.createAddress({ ...data, accountId });
    },
    onSuccess: () => {
      toast({ title: 'Endereço adicionado', description: 'O endereço foi salvo com sucesso!' });
      queryClient.invalidateQueries({ queryKey: ['addresses', accountId] });
    },
    onError: () => {
      toast({
        title: 'Erro',
        description: 'Ocorreu um erro ao adicionar o endereço.',
        variant: 'destructive',
      });
    },
  });

  const updateAddressMutation = useMutation({
    mutationFn: ({ addressId, data }: { addressId: string; data: LocalAddress }) => {
      return accountApi.updateAddress(addressId, { ...data, accountId: accountId! });
    },
    onSuccess: () => {
      toast({
        title: 'Endereço atualizado',
        description: 'O endereço foi atualizado com sucesso!',
      });
      queryClient.invalidateQueries({ queryKey: ['addresses', accountId] });
    },
    onError: () => {
      toast({
        title: 'Erro',
        description: 'Ocorreu um erro ao atualizar o endereço.',
        variant: 'destructive',
      });
    },
  });

  const saveDeliveryAddressMutation = useMutation({
    mutationFn: (addressId: string | null) => {
      if (!proposalId) throw new Error('Proposal ID is required');
      return proposalApi.update(proposalId, { deliveryAddressId: addressId });
    },
    onSuccess: (_data, addressId) => {
      onSelectAddress(addressId);
      queryClient.invalidateQueries({ queryKey: ['proposal'] });
    },
    onError: () => {
      toast({
        title: 'Erro',
        description: 'Não foi possível salvar o endereço de entrega.',
        variant: 'destructive',
      });
    },
  });

  const deleteAddressMutation = useMutation({
    mutationFn: (addressId: string) => accountApi.deleteAddress(addressId),
    onSuccess: (_data, deletedId) => {
      toast({ title: 'Endereço removido', description: 'O endereço foi excluído com sucesso!' });
      queryClient.invalidateQueries({ queryKey: ['addresses', accountId] });
      if (selectedAddressId === deletedId && proposalId) {
        saveDeliveryAddressMutation.mutate(null);
      }
      setDeleteAddressId(null);
    },
    onError: () => {
      toast({
        title: 'Erro',
        description: 'Ocorreu um erro ao excluir o endereço.',
        variant: 'destructive',
      });
    },
  });

  const handleSubmitAddress = (localAddress: LocalAddress) => {
    if (editingAddress) {
      updateAddressMutation.mutate({ addressId: editingAddress.id, data: localAddress });
    } else {
      createAddressMutation.mutate(localAddress);
    }
    setAddressDialogOpen(false);
    setEditingAddress(null);
  };

  const handleOpenCreateDialog = () => {
    setEditingAddress(null);
    setAddressDialogOpen(true);
  };

  const handleOpenEditDialog = (address: LocalAddress) => {
    setEditingAddress(address);
    setAddressDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setAddressDialogOpen(false);
    setEditingAddress(null);
  };

  const handleCopyAddress = async (address: AccountAddress) => {
    try {
      const { street, number, district, city, state, zip_code, complement, reference_point } =
        address;
      let text = `${street}, ${number}, ${district}\n${city} - ${state}, ${zip_code}`;
      if (complement) text += `\nComplemento: ${complement}`;
      if (reference_point) text += `\nPonto de Referência: ${reference_point}`;
      await navigator.clipboard.writeText(text);
      toast({ title: 'Sucesso!', description: 'Endereço copiado para a área de transferência.' });
    } catch {
      toast({
        title: 'Erro',
        description: 'Não foi possível copiar o endereço.',
        variant: 'destructive',
      });
    }
  };

  const handleSelectAddress = (addressId: string) => {
    const newAddressId = selectedAddressId === addressId ? null : addressId;
    saveDeliveryAddressMutation.mutate(newAddressId);
  };

  const handleDeleteAddress = (addressId: string) => {
    setDeleteAddressId(addressId);
  };

  if (!accountId) {
    return (
      <div className="flex items-center justify-center rounded-lg border border-dashed border-gray-300 py-12">
        <p className="text-sm text-muted-foreground">
          Selecione um cliente antes de gerenciar endereços.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const isEmpty = addresses.length === 0;

  return (
    <>
      {isEmpty ? (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Ainda não há endereços cadastrados para este cliente!
          </p>
          <Button
            className="bg-black text-white hover:bg-gray-800"
            onClick={handleOpenCreateDialog}
          >
            <Plus className="mr-2 h-4 w-4" />
            Adicionar endereço
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Selecione um endereço de entrega:</p>
            <Button
              className="bg-black text-white hover:bg-gray-800"
              onClick={handleOpenCreateDialog}
            >
              <Plus className="mr-2 h-4 w-4" />
              Adicionar endereço
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {addresses.map((address) => {
              const isSelected = selectedAddressId === address.id;
              const addressParts = formatAddressParts(address);
              const isFiscalApi = address.type?.includes('Fiscal') ?? false;

              return (
                <Card
                  key={address.id}
                  className={cn(
                    'flex flex-col p-4 rounded-lg transition-all duration-200 cursor-pointer',
                    isSelected
                      ? 'border-2 border-primary shadow-md bg-primary/5'
                      : 'border border-gray-200 shadow-sm hover:shadow-md',
                  )}
                  onClick={() => handleSelectAddress(address.id)}
                >
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex flex-wrap gap-1">
                      {address.type &&
                        address.type.length > 0 &&
                        address.type.map((type) => (
                          <Badge key={type} variant="outline" className="text-xs font-medium">
                            {type}
                          </Badge>
                        ))}
                    </div>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => handleSelectAddress(address.id)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Selecionar endereço ${address.street}`}
                    />
                  </div>

                  <div className="flex-1 space-y-2">
                    {addressParts.map((part, index) => (
                      <div
                        key={part}
                        className={
                          index === 0 ? 'font-medium text-sm' : 'text-sm text-muted-foreground'
                        }
                      >
                        {part}
                      </div>
                    ))}

                    {(address.complement || address.reference_point) && (
                      <>
                        <Separator className="my-3" />
                        <div className="space-y-1">
                          {address.complement && (
                            <div className="text-xs text-muted-foreground">
                              <span className="font-medium text-foreground">Complemento:</span>{' '}
                              {address.complement}
                            </div>
                          )}
                          {address.reference_point && (
                            <div className="text-xs text-muted-foreground">
                              <span className="font-medium text-foreground">
                                Ponto de Referência:
                              </span>{' '}
                              {address.reference_point}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t border-gray-100">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-foreground"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyAddress(address);
                            }}
                            aria-label="Copiar endereço"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          <p>Copiar</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    {!isFiscalApi && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEditDialog(address);
                              }}
                              aria-label="Editar endereço"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <p>Editar</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}

                    {!isFiscalApi && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteAddress(address.id);
                              }}
                              aria-label="Excluir endereço"
                            >
                              <Trash className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <p>Excluir</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      <AddressFormDialog
        open={addressDialogOpen}
        onClose={handleCloseDialog}
        onSubmit={handleSubmitAddress}
        mode={editingAddress ? 'edit' : 'create'}
        address={editingAddress ?? undefined}
      />

      <AlertDialog
        open={!!deleteAddressId}
        onOpenChange={(open) => !open && setDeleteAddressId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir endereço</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este endereço? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={() => deleteAddressId && deleteAddressMutation.mutate(deleteAddressId)}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
